import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { ArrowRight, Users, Target, ShieldAlert, TrendingDown, Hammer, Sparkles, Network, ClipboardCheck, Layers, Activity, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ImageWithFallback } from '../components/figma/ImageWithFallback';
import { OrganicShapes } from '../components/decor/OrganicShapes';
import { Reveal } from '../components/Reveal';

const outcomes = [
  {
    icon: Target,
    title: 'Close care gaps in time, not after the year locks',
    modules: 'HEDISIQ, Care Management',
  },
  {
    icon: TrendingDown,
    title: 'Catch HCC/RAF documentation gaps before revenue leaks',
    modules: 'Risk Adjustment Tools',
  },
  {
    icon: Users,
    title: 'Stop attribution drift and double-counting',
    modules: 'Attribution Hub',
  },
  {
    icon: ShieldAlert,
    title: "See shared-savings risk while there's still time to act",
    modules: 'AccountableIQ',
  },
];

const solutions = [
  {
    icon: Hammer,
    title: 'Forward Deployed Engineering (FDE)',
    description: "We embed inside your clinical and claims workflows, working in your existing tools, and stay accountable from first build through production.",
    bullets: ['Embedded in your workflows, not a template', 'Accountable through production, not just at launch'],
    color: 'text-[#34AFFB]',
    bgColor: 'bg-[#34AFFB]/10',
  },
  {
    icon: Sparkles,
    title: 'Agentic AI',
    description: 'We build AI agents that take the work off your team entirely, closing the loop on clinical and administrative tasks autonomously, with human oversight where the stakes demand it.',
    bullets: ['Agents that automate real clinical & admin workflows', 'Autonomy where it helps, human-in-the-loop where it matters'],
    color: 'text-[#70D6C5]',
    bgColor: 'bg-[#70D6C5]/10',
  },
  {
    icon: Activity,
    title: 'Population Health Analytics',
    description: "Predictive risk-stratification models and SDOH-informed analytics trained on your population's data, not a generic template.",
    bullets: ['Risk models trained on your own data', 'Predictive flags for proactive outreach'],
    color: 'text-[#FF7F66]',
    bgColor: 'bg-[#FF7F66]/10',
  },
  {
    icon: ClipboardCheck,
    title: 'Risk-adjustment Intelligence',
        description: 'We catch the HCC/RAF gaps that quietly drain revenue, flagging missed and unsupported codes at the point of care, so accurate documentation happens before the claim is filed.',
    bullets: ['Missed & unsupported codes flagged in real time', 'Every code tied to audit-defensible evidence'],
    color: 'text-[#34AFFB]',
    bgColor: 'bg-[#34AFFB]/10',
  },
  {
    icon: Layers,
    title: 'Ontologies & Semantic Data Models',
    description: 'We map fragmented EHR, claims, and ADT data to a shared ontology, one FHIR-native model where entities, relationships, and meaning are defined once, so every downstream model and agent reasons over the same trusted truth.',
    bullets: ['A unifying ontology across EHR, claims & ADT', 'Normalized vocabularies, matched patients, one shared meaning'],
    color: 'text-[#70D6C5]',
    bgColor: 'bg-[#70D6C5]/10',
  },
  {
    icon: Network,
    title: 'Network Data & Benchmarking',
    description: 'We move care and performance data across organizational lines between affiliated practices, IPAs, and ACO partners, so every group sees where it stands against the network.',
    bullets: ['Data exchange across organizational boundaries', 'Apples-to-apples benchmarking across partners'],
    color: 'text-[#FF7F66]',
    bgColor: 'bg-[#FF7F66]/10',
  },
];

const practice = [
  {
    category: 'Downside-Risk ACO',
    title: 'Risk Surfaced Before the Year Locked',
    description: "For a downside-risk ACO, we flagged HCC/RAF gaps and rising-risk patients mid-year — turning a blind spot that used to surface at reconciliation into decisions made while there was still time to act.",
  },
  {
    category: 'IPA / MSO',
    title: 'One Ontology Across a Fragmented Network',
    description: 'For an IPA spanning dozens of independent practices on different EHRs, we mapped clinical and claims data to a single shared ontology — so population health and reporting finally ran on one coherent source, not a dozen.',
  },
  {
    category: 'Medical Group',
    title: 'Agents That Cleared the Admin Backlog',
    description: "For a medical group, we deployed AI agents to take routine documentation and coordination off staff — closing the loop on work that used to pile up, so clinicians spent their time on patients, not paperwork.",
  },
];

export function ProvidersACOsPage() {
  return (
    <div className="min-h-screen">
      <Header />

      {/* 1. Their reality */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#f9fbfc] via-white to-[#F4F7F9] py-12 lg:py-16">
        <OrganicShapes variant="hero" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal as="div" direction="right">
              <span className="border-l-4 border-[#34AFFB] pl-3 text-[#34AFFB] text-sm font-semibold inline-block mb-6">
                Providers & ACOs
              </span>
              <h1 className="text-5xl lg:text-6xl text-[#1f2937] mb-6 leading-tight">
                Close care gaps while they're still open
              </h1>
              <p className="text-xl text-[#4B5563] mb-8 leading-relaxed">
                For medical groups, IPAs/MSOs, FQHCs, and downside-risk ACOs: sharper decisions, less administrative drag. We turn scattered clinical and claims data into guidance that arrives in time to close care gaps, catch HCC/RAF documentation gaps and attribution drift before they become revenue loss, and surface shared-savings risk while there's still time to act.
              </p>
              <Button asChild size="lg" className="bg-[#34AFFB] hover:bg-[#34AFFB]/90 text-white">
                <Link to="/#contact">
                  See how this works for your organization
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </Reveal>
            <Reveal as="div" direction="left" delay={150} className="hidden lg:block">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <ImageWithFallback
                  src="https://images.unsplash.com/photo-1763310225009-50214e3c99d9?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080"
                  alt="Healthcare providers and clinical care"
                  className="w-full h-[500px] object-cover"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 2. Outcomes → modules */}
      <section className="py-20 bg-gradient-to-b from-white to-[#f9fbfc]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <h2 className="text-4xl text-[#1f2937] mb-4">Outcomes, powered by HealthBridge</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {outcomes.map((o, index) => {
              const Icon = o.icon;
              return (
                <Reveal as="div" key={index} delay={(index % 2) * 120} className="bg-white rounded-2xl p-8 border-l-4 border-[#34AFFB]/50 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex items-start gap-6">
                  <div className="h-14 w-14 bg-[#34AFFB]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon className="h-7 w-7 text-[#34AFFB]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#1f2937] mb-2 text-xl leading-snug">{o.title}</h3>
                    <p className="text-[#34AFFB] text-sm uppercase tracking-wide font-semibold">{o.modules}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 3. Solutions: data & AI services beyond HealthBridge */}
      <section className="py-20 bg-[#F4F7F9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <h2 className="text-4xl text-[#1f2937] mb-4">Applied Data & AI Solutions</h2>
            <p className="text-xl text-[#4B5563] max-w-3xl mx-auto">
              For the problems off-the-shelf products don't solve. We build custom data pipelines, models, and AI agents for providers, ACOs, and the teams carrying risk, engineered for your data and owned by you.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <Reveal as="div" key={index} delay={(index % 3) * 120} className="bg-white rounded-2xl p-12 shadow-sm">
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`h-12 w-12 ${solution.bgColor} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`h-6 w-6 ${solution.color}`} />
                    </div>
                    <h3 className="text-xl font-semibold text-[#1f2937]">{solution.title}</h3>
                  </div>
                  <p className="text-[#4B5563] leading-relaxed mb-6">{solution.description}</p>
                  <ul className="space-y-3">
                    {solution.bullets.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-[#4B5563] text-sm leading-relaxed">
                        <CheckCircle className="h-4 w-4 text-[#70D6C5] flex-shrink-0 mt-1" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* From recent engagements */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <h2 className="text-4xl mb-4 text-[#1f2937]">What this looks like in practice</h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
            {[
              {
                tag: 'Behavioral Health',
                title: 'Care Coordination That Pays for Itself',
                body: 'For a behavioral health practice, we stood up an RTM program that captures the right CPT codes for between-visit coordination, turning patient support that used to be unbillable into a sustainable revenue line.',
                stats: [
                  { value: '$210K', label: 'New annual RTM revenue' },
                  { value: '88%', label: 'Eligible patients enrolled' },
                ],
              },
              {
                tag: 'Digital Health',
                title: 'Data Platform, Built to Launch',
                body: "We've helped a digital health team go from concept to a working, branded care platform giving them the foundation to run their own model without building infrastructure from scratch.",
                stats: [
                  { value: '14 wks', label: 'Concept to launch' },
                  { value: '1', label: 'Platform replacing 4 point tools' },
                ],
              },
              {
                tag: 'Community Care',
                title: 'AI That Gives Nurses Their Time Back',
                body: "We've used Agentic AI to pull from the EMR, visit notes, and patient history to auto-populate care plans across a population turning hours of manual chart review into minutes so nurses spend time on patients, not paperwork.",
                stats: [
                  { value: '75%', label: 'Less time on chart review' },
                  { value: '500+ hrs', label: 'Given back per quarter' },
                ],
              },
            ].map((story, index) => (
              <div
                key={index}
                style={{
                  paddingTop: '2rem',
                  paddingBottom: '2rem',
                  paddingLeft: index === 0 ? '0' : '4rem',
                  paddingRight: index === 2 ? '0' : '4rem',
                  borderLeft: index > 0 ? '1px solid #cbd5e1' : 'none',
                }}
              >
                <span style={{ display: 'block', fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#1F2937', marginBottom: '1rem' }}>
                  {story.tag}
                </span>
                <h3 className="text-xl font-semibold text-[#1f2937] mb-4 leading-snug">{story.title}</h3>
                <p className="text-[#4B5563] text-sm leading-relaxed">{story.body}</p>
                <div style={{ display: 'flex', gap: '2rem', marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb' }}>
                  {story.stats.map((stat, i) => (
                    <div key={i}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#34AFFB' }}>{stat.value}</div>
                      <div style={{ fontSize: '11px', color: '#4B5563' }}>{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Proof */}
      <section className="py-16 bg-[#F4F7F9]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-12">
            <h2 className="text-2xl text-[#1f2937] mb-4">Patients are the payoff.</h2>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              When the data connects and the workflow flows, care becomes more proactive, coherent, and whole.
            </p>
          </Reveal>
          <div className="ph-two-up">
            {[
              { stat: 'Up to 25%', label: 'Reduction in care-gap closure time' },
              { stat: 'Up to 15%', label: 'Improvement in HCC recapture' },
            ].map((item, index) => (
              <Reveal as="div" key={index} direction="scale" delay={index * 130} className="bg-white rounded-2xl p-8 text-center shadow-sm">
                <div className="text-3xl font-bold text-[#34AFFB] mb-3">{item.stat}</div>
                <p className="text-[#4B5563] text-sm">{item.label}</p>
              </Reveal>
            ))}
          </div>
          
        </div>
      </section>

      {/* 6. CTA */}
      <section className="relative overflow-hidden py-20 bg-gradient-to-r from-[#34AFFB] to-[#70D6C5]">
        <OrganicShapes variant="band" />
        <Reveal className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl text-white mb-4">See how this works for your organization.</h2>
          <Button asChild size="lg" className="bg-white text-[#34AFFB] hover:bg-white/90">
            <Link to="/#contact">
              Book a demo
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
}
